import {getTestProps} from '@benchling/js-test-utils';
import {noop} from '@benchling/js-utils';
import React from 'react';

import {ContainerSummary, StorableSummary} from 'constants/SampleConstants';
import {SampleRestrictionStatus} from 'constants/SampleStatusConstants';
import SampleControlFieldsSection from 'inventory/sample-status/SampleControlFieldsSection';
import StorableSearchInput from 'samples/storables/StorableSearchInput';
import {
  ConfirmSampleControlsPaneState,
  ConfirmTransferQuantitiesData,
  UpdateTransferModalStateHandler,
} from 'samples/transfers/TransferModalUtil';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import FormGroup from 'ui-library/bs/FormGroup';
import {Title} from 'ui-library/Headers';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import RadioButtonGroup from 'ui-library/inputs/RadioButtonGroup';

export interface ConfirmSampleControlsPaneProps {
  paneState: ConfirmSampleControlsPaneState;
  onUpdateModalState: UpdateTransferModalStateHandler;
}

// TODO(BNCH-58990) Use standard doesItemSupportSampleStatus utils once container summary knows about
// whether it's a well.
// This should not be used elsewhere unless absolutely necessary - prefer doesItemSupportSampleStatus or
// otherwise relying on whether a container is actually a well, not just an N/A restriction status.
export function supportsValidRestrictionStatus(container: ContainerSummary): boolean {
  return container.restrictionStatus !== SampleRestrictionStatus.NOT_APPLICABLE;
}

function getNumberOfOutputFixedPlateWells(confirmTransferData: ConfirmTransferQuantitiesData | null): number {
  if (!confirmTransferData) {
    return 0;
  }
  return confirmTransferData.filter(({outputContainer}) => !supportsValidRestrictionStatus(outputContainer))
    .length;
}

export default function ConfirmSampleControlsPane(props: ConfirmSampleControlsPaneProps): JSX.Element {
  const source = props.paneState.selectedInputContainer;
  const sourceDisplay =
    source != null ? (
      <FormGroup>
        <FieldLabel label="Source container" labelStyle={FieldLabel.STYLES.LABEL} />
        <StorableSearchInput<StorableSummary> onChange={noop} selectedStorables={[source]} disabled={true} />
      </FormGroup>
    ) : null;
  const hasSourceContainer = source != null && supportsValidRestrictionStatus(source);
  const numberOfWellOutputs = getNumberOfOutputFixedPlateWells(props.paneState.confirmTransferQuantitiesData);

  return (
    <div {...getTestProps({component: 'ConfirmSampleControlsPane', element: 'root'})}>
      {numberOfWellOutputs > 0 && (
        <div className="u-small-spacing-bottom">
          <InlineBanner
            bannerType={InlineBannerType.WARNING}
            text="Well plate wells don't have sample controls and will be excluded from this step."
          />
        </div>
      )}
      <FormGroup>
        <RadioButtonGroup
          title="Destination sample controls"
          options={[
            {
              label: hasSourceContainer
                ? 'Add values from source container'
                : 'Keep existing values on destination containers',
              value: true,
            },
            {
              label: 'Manually set values',
              value: false,
            },
          ]}
          onChange={(useInferredSampleControls: boolean) => {
            props.onUpdateModalState({useInferredSampleControls});
          }}
          selectedValue={props.paneState.useInferredSampleControls}
          isVertical={true}
        />
      </FormGroup>
      {props.paneState.useInferredSampleControls && hasSourceContainer && source != null ? (
        <SampleControlFieldsSection
          extraFieldElement={sourceDisplay}
          restrictionStatus={source.restrictionStatus}
          restrictedSampleUsers={source.sampleRoles?.restrictedSampleUsers ?? []}
          sampleOwners={source.sampleRoles?.sampleOwners ?? []}
          onChangeSampleControls={noop}
          isReadOnly={true}
        />
      ) : !props.paneState.useInferredSampleControls ? (
        <SampleControlFieldsSection
          restrictionStatus={props.paneState.restrictionStatus}
          restrictedSampleUsers={props.paneState.restrictedSampleUsers}
          sampleOwners={props.paneState.sampleOwners}
          onChangeSampleControls={props.onUpdateModalState}
        />
      ) : null}
      {props.paneState.errorMessage && (
        <div>
          <Title>Errors:</Title>
          <div className="u-danger-color">{props.paneState.errorMessage}</div>
        </div>
      )}
    </div>
  );
}
